import React, { useState, useEffect, useCallback } from 'react';
import { CardElement, useStripe, useElements } from '@stripe/react-stripe-js';
import { client, urlFor } from '../client'; // Assuming client.js is in src folder

// --- Helper: Loading Spinner ---
const LoadingSpinner = ({ message = 'Loading products...' }) => (
  <div className="flex flex-col justify-center items-center min-h-[300px]">
    <div className="animate-spin rounded-full h-12 w-12 border-t-4 border-b-4 border-primary mb-4"></div>
    <p className="text-lg text-gray-700">{message}</p>
  </div>
);

// --- Helper: Error Message ---
const ErrorDisplay = ({ message }) => (
  <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded relative mb-4" role="alert">
    <strong className="font-bold">Error: </strong>
    <span className="block sm:inline">{message || 'An unexpected error occurred.'}</span>
  </div>
);

const OnlineStorePage = () => {
  const stripe = useStripe();
  const elements = useElements();

  // --- Sanity Data State ---
  const [categories, setCategories] = useState([]);
  const [products, setProducts] = useState([]);
  const [dataLoading, setDataLoading] = useState(true);
  const [dataError, setDataError] = useState(null);

  // --- Form & Cart State ---
  const [studentFirstName, setStudentFirstName] = useState('');
  const [studentLastName, setStudentLastName] = useState('');
  const [studentClass, setStudentClass] = useState('');
  const [confirmationEmail, setConfirmationEmail] = useState('');
  const [cardholderName, setCardholderName] = useState('');
  
  const [cart, setCart] = useState({}); // Object to store { productId: { quantity, size, name, price, image, sizes } }
  const [totalAmount, setTotalAmount] = useState(0);

  // --- Stripe Payment State ---
  const [processing, setProcessing] = useState(false);
  const [paymentError, setPaymentError] = useState(null);
  const [paymentSucceeded, setPaymentSucceeded] = useState(false);
  const [clientSecret, setClientSecret] = useState('');

  // --- Fetch Categories and Products from Sanity --- 
  useEffect(() => {
    const fetchStoreData = async () => {
      setDataLoading(true);
      setDataError(null);
      try {
        const categoriesQuery = `*[_type == "productCategory"] | order(title asc) {
          _id,
          title,
          slug,
          description,
          "products": *[_type == "product" && references(^._id)] | order(name asc) {
            _id,
            name,
            slug,
            price,
            description,
            image,
            sizes,
            category->{_id, title}
          }
        }`;
        const fetchedCategories = await client.fetch(categoriesQuery);
        
        // Store categories and a flat list of all products for easier access
        setCategories(fetchedCategories);
        const allProducts = fetchedCategories.reduce((acc, category) => {
          category.products.forEach(p => acc[p._id] = p);
          return acc;
        }, {});
        setProducts(allProducts);

      } catch (err) {
        console.error("Failed to fetch store data from Sanity:", err);
        setDataError('Could not load products. Please try refreshing the page.');
      }
      setDataLoading(false);
    };
    fetchStoreData();
  }, []);

  // --- Calculate Total Amount & Fetch Payment Intent --- 
  const updateCartAndTotal = useCallback(() => {
    let currentTotal = 0;
    Object.values(cart).forEach(item => {
      currentTotal += item.quantity * item.price;
    });
    setTotalAmount(currentTotal);

    if (currentTotal === 0) {
        setClientSecret(''); // Clear client secret if cart is empty
    }
    // Removed payment intent creation from here
  }, [cart]); // Dependencies are now just [cart]

  useEffect(() => {
    updateCartAndTotal();
  }, [cart, updateCartAndTotal]); // updateCartAndTotal's own dependencies ensure this is correct
  
  // --- Cart Management Functions ---
  const handleQuantityChange = (productId, newQuantity) => {
    const product = products[productId];
    if (!product) return;

    const quantity = Math.max(0, parseInt(newQuantity, 10) || 0);

    setCart(prevCart => {
      const updatedCart = { ...prevCart };
      if (quantity === 0) {
        delete updatedCart[productId];
      } else {
        updatedCart[productId] = {
          ...(updatedCart[productId] || {}), // Keep existing size if any
          quantity,
          price: product.price,
          name: product.name,
          image: product.image,
          sizes: product.sizes || [], // Ensure sizes is an array
          // Initialize size if not set and product has sizes
          size: (updatedCart[productId]?.size || product.sizes?.length > 0) ? (updatedCart[productId]?.size || product.sizes[0]) : 'N/A' 
        };
      }
      return updatedCart;
    });
  };

  const handleSizeChange = (productId, newSize) => {
    setCart(prevCart => {
      if (!prevCart[productId]) return prevCart; // Should not happen if item in cart
      return {
        ...prevCart,
        [productId]: {
          ...prevCart[productId],
          size: newSize,
        },
      };
    });
  };

  // --- Handle Form Submission ---
  const handleSubmit = async (event) => {
    event.preventDefault();
    
    if (!stripe || !elements) {
      setPaymentError("Stripe has not loaded yet. Please wait and try again.");
      return;
    }
    if (totalAmount === 0) {
      setPaymentError("Please add items to your cart before paying.");
      return;
    }
    if (!studentFirstName || !studentLastName || !studentClass || !confirmationEmail || !cardholderName) {
      setPaymentError("Please fill in all required fields: Student Information, Confirmation Email, and Cardholder Name.");
      return;
    }

    setProcessing(true);
    setPaymentError(null);
    let currentClientSecret = ''; // We will fetch a new one

    const orderDetails = {
      amount: Math.round(totalAmount * 100), // Amount in cents
      studentFirstName,
      studentLastName,
      studentClass,
      confirmationEmail,
      items: Object.values(cart).filter(item => item.quantity > 0).map(item => ({
        name: item.name,
        quantity: item.quantity,
        size: item.size || 'N/A' 
      }))
    };

    try {
      const response = await fetch('/.netlify/functions/create-payment-intent', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(orderDetails)
      });
      const data = await response.json();
      if (data.error) {
        setPaymentError(data.error);
        setClientSecret(''); // Ensure clientSecret is cleared on error
        setProcessing(false);
        return;
      }
      currentClientSecret = data.clientSecret;
      setClientSecret(data.clientSecret); // Store it, might be useful if retry without changing details
    } catch (err) {
      console.error("Error fetching client secret:", err);
      setPaymentError("Failed to initialize payment. Please check your details or try refreshing.");
      setClientSecret(''); // Ensure clientSecret is cleared on error
      setProcessing(false);
      return;
    }
    
    if (!currentClientSecret) {
        setPaymentError("Could not retrieve payment session. Please try again.");
        setProcessing(false);
        return;
    }

    const cardElement = elements.getElement(CardElement);
    if (!cardElement) {
        setPaymentError("Card details are not available. Please ensure the card form is visible.");
        setProcessing(false);
        return;
    }

    const { error: paymentMethodError, paymentMethod } = await stripe.createPaymentMethod({
        type: 'card',
        card: cardElement,
        billing_details: {
            name: cardholderName,
            email: confirmationEmail,
        },
    });

    if (paymentMethodError) {
        setPaymentError(`Payment method creation failed: ${paymentMethodError.message}`);
        setProcessing(false);
        return;
    }

    const { error: confirmError, paymentIntent } = await stripe.confirmCardPayment(currentClientSecret, {
        payment_method: paymentMethod.id,
        receipt_email: confirmationEmail
    });

    if (confirmError) {
      setPaymentError(`Payment failed: ${confirmError.message}`);
      setProcessing(false);
    } else {
      if (paymentIntent.status === 'succeeded') {
        setPaymentError(null);
        setProcessing(false);
        setPaymentSucceeded(true);
        // console.log('Payment Succeeded:', paymentIntent);
        // Optional: Clear cart or redirect
        // setCart({}); 
      } else {
          setPaymentError(`Payment status: ${paymentIntent.status}. Please contact support.`);
          setProcessing(false);
      }
    }
  };

  const cardElementOptions = {
    style: {
      base: {
        color: "#32325d",
        fontFamily: '"Helvetica Neue", Helvetica, sans-serif',
        fontSmoothing: "antialiased",
        fontSize: "16px",
        "::placeholder": { color: "#aab7c4" }
      },
      invalid: { color: "#fa755a", iconColor: "#fa755a" }
    }
  };

  if (dataLoading) return <LoadingSpinner />;
  if (dataError) return <ErrorDisplay message={dataError} />;
  if (categories.length === 0) return <p className="text-center text-gray-600 py-10">No product categories found. Please check back later.</p>;

  return (
    <div className="container mx-auto px-4 py-8 max-w-4xl">
      <h1 className="text-3xl font-bold text-primary mb-8 text-center">Gym Shorts/Pants & Spirit Wear Order</h1>

      <form onSubmit={handleSubmit} className="space-y-12 bg-white p-6 md:p-8 rounded-lg shadow-xl">
        {/* --- Student Information --- */}
        <section>
          <h2 className="text-2xl font-semibold text-primary mb-4 border-b pb-2">Student Information</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
            <div>
              <label htmlFor="studentFirstName" className="block text-sm font-medium text-gray-700 mb-1">Student's First Name <span className="text-red-500">*</span></label>
              <input type="text" id="studentFirstName" value={studentFirstName} onChange={(e) => setStudentFirstName(e.target.value)} required className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-primary focus:border-primary" />
            </div>
            <div>
              <label htmlFor="studentLastName" className="block text-sm font-medium text-gray-700 mb-1">Student's Last Name <span className="text-red-500">*</span></label>
              <input type="text" id="studentLastName" value={studentLastName} onChange={(e) => setStudentLastName(e.target.value)} required className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-primary focus:border-primary" />
            </div>
          </div>
          <div className="mb-4">
              <label htmlFor="studentClass" className="block text-sm font-medium text-gray-700 mb-1">Student's Class <span className="text-red-500">*</span></label>
              <input type="text" id="studentClass" value={studentClass} onChange={(e) => setStudentClass(e.target.value)} required className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-primary focus:border-primary" />
          </div>
           <div>
              <label htmlFor="confirmationEmail" className="block text-sm font-medium text-gray-700 mb-1">Confirmation Email <span className="text-red-500">*</span></label>
              <input type="email" id="confirmationEmail" value={confirmationEmail} onChange={(e) => setConfirmationEmail(e.target.value)} required className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-primary focus:border-primary" />
          </div>
        </section>

        {/* --- Products by Category --- */}
        {categories.map(category => (
          category.products && category.products.length > 0 && (
            <section key={category._id}>
              <h2 className="text-2xl font-semibold text-primary mb-6 border-b pb-2">{category.title}</h2>
              {category.description && <p className="text-gray-600 mb-6">{category.description}</p>}
              <div className="space-y-6">
                {category.products.map(product => (
                  <div key={product._id} className="flex flex-col md:flex-row items-center space-y-4 md:space-y-0 md:space-x-6 p-4 border rounded-lg shadow-sm bg-slate-50">
                    {product.image && (
                      <img 
                        src={urlFor(product.image).width(120).height(120).fit('crop').url()} 
                        alt={product.name} 
                        className="w-32 h-32 md:w-28 md:h-28 object-cover rounded-md border" 
                        loading="lazy"
                      />
                    )}
                    {!product.image && <div className="w-32 h-32 md:w-28 md:h-28 bg-gray-200 rounded-md flex items-center justify-center text-gray-500 text-sm">No Image</div>}
                    <div className="flex-grow text-center md:text-left">
                      <h3 className="text-xl font-medium text-gray-800">{product.name}</h3>
                      {product.description && <p className="text-sm text-gray-600 mt-1 mb-2">{product.description}</p>}
                      <p className="text-lg font-semibold text-primary">${product.price.toFixed(2)}</p>
                    </div>
                    <div className="w-24">
                      <label htmlFor={`quantity-${product._id}`} className="block text-sm font-medium text-gray-700 mb-1 text-center">Quantity</label>
                      <input 
                        type="number" 
                        id={`quantity-${product._id}`} 
                        min="0" 
                        value={cart[product._id]?.quantity || 0} 
                        onChange={(e) => handleQuantityChange(product._id, e.target.value)} 
                        className="w-full px-2 py-1.5 border border-gray-300 rounded-md text-center focus:ring-primary focus:border-primary"
                      />
                    </div>
                    {(product.sizes && product.sizes.length > 0) && (
                      <div className="w-full md:w-48">
                        <label htmlFor={`size-${product._id}`} className="block text-sm font-medium text-gray-700 mb-1">Size</label>
                        <select 
                          id={`size-${product._id}`} 
                          value={cart[product._id]?.size || product.sizes[0]} 
                          onChange={(e) => handleSizeChange(product._id, e.target.value)} 
                          disabled={!cart[product._id] || cart[product._id]?.quantity === 0}
                          className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-primary focus:border-primary disabled:bg-gray-100"
                        >
                          {product.sizes.map(size => <option key={size} value={size}>{size}</option>)}
                        </select>
                      </div>
                    )}
                    {(!product.sizes || product.sizes.length === 0) && <div className="w-full md:w-48 h-[42px]"></div>} {/* Placeholder for alignment if no sizes */}
                  </div>
                ))}
              </div>
            </section>
          )
        ))}

        {/* --- Order Summary --- */}
        <section>
            <h2 className="text-2xl font-semibold text-primary mb-4 border-b pb-2">Order Summary</h2>
            {Object.keys(cart).length === 0 && <p className="text-gray-600">Your cart is empty.</p>}
            {Object.keys(cart).length > 0 && (
                <div className="space-y-2 mb-4 text-gray-700">
                    {Object.entries(cart).map(([productId, item]) => (
                        item.quantity > 0 && (
                            <div key={productId} className="flex justify-between items-center">
                                <span>{item.name} ({item.size}) x {item.quantity}</span>
                                <span>${(item.quantity * item.price).toFixed(2)}</span>
                            </div>
                        )
                    ))}
                    <div className="flex justify-between font-bold text-xl border-t pt-3 mt-3 text-primary">
                        <span>Total</span>
                        <span>${totalAmount.toFixed(2)}</span>
                    </div>
                </div>
            )}
        </section>

        {/* --- Payment Information --- */}
        <section>
            <h2 className="text-2xl font-semibold text-primary mb-4 border-b pb-2">Payment</h2>
            {totalAmount > 0 && !paymentSucceeded && (
              <div className="space-y-4">
                {/* Cardholder Name and Card Element are always visible if totalAmount > 0 and not succeeded */}
                <div>
                  <label htmlFor="cardholderName" className="block text-sm font-medium text-gray-700 mb-1">Cardholder Name <span className="text-red-500">*</span></label>
                  <input type="text" id="cardholderName" value={cardholderName} onChange={(e) => setCardholderName(e.target.value)} required className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-primary focus:border-primary" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Credit or debit card <span className="text-red-500">*</span></label>
                  <div className="p-3 border border-gray-300 rounded-md bg-white shadow-sm">
                      <CardElement options={cardElementOptions} />
                  </div>
                </div>
                <p className="text-sm text-green-600 flex items-center mt-2">
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 mr-1.5" viewBox="0 0 20 20" fill="currentColor"><path fillRule="evenodd" d="M10 1a4.5 4.5 0 00-4.5 4.5V9H5a2 2 0 00-2 2v6a2 2 0 002 2h10a2 2 0 002-2v-6a2 2 0 00-2-2h-.5V5.5A4.5 4.5 0 0010 1zm3 8V5.5a3 3 0 10-6 0V9h6z" clipRule="evenodd" /></svg>
                    All transactions are secure and encrypted.
                </p>
              </div>
            )}
            {totalAmount === 0 && !paymentSucceeded && <p className="text-center text-gray-500">Please add items to your cart to proceed with payment.</p>}
            
            {paymentError && <ErrorDisplay message={paymentError} />}
            {paymentSucceeded && (
              <div className="bg-green-100 border-l-4 border-green-500 text-green-700 p-4 mt-4" role="alert">
                <p className="font-bold text-xl">Payment Successful!</p>
                <p>Thank you for your order. A confirmation email will be sent to {confirmationEmail}.</p>
              </div>
            )}

            <button
                type="submit"
                disabled={!stripe || processing || paymentSucceeded || totalAmount === 0 || !studentFirstName || !studentLastName || !studentClass || !confirmationEmail || !cardholderName}
                className={`w-full mt-8 py-3 px-4 border border-transparent rounded-md shadow-sm text-lg font-medium text-white transition-colors duration-150 ${(!stripe || processing || paymentSucceeded || totalAmount === 0 || !studentFirstName || !studentLastName || !studentClass || !confirmationEmail || !cardholderName) ? 'bg-gray-400 cursor-not-allowed' : 'bg-primary hover:bg-primary/90 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary'}`}
            >
                {processing ? 'Processing Payment...' : `Pay $${totalAmount.toFixed(2)} Securely`}
            </button>
        </section>
      </form>
    </div>
  );
};

export default OnlineStorePage; 